import { createSlice } from "@reduxjs/toolkit";
import { RootState, StoreDispatch } from "../store";
import {
  aggGroupRelationship,
  connectUserToRegion,
  createGroup,
  CreateGroupRequest,
  createGroupWallet,
  getGroupList,
  getRegionUsers,
  removeUserFromRegion,
  SearchGroupListRequest,
  updateGroup,
  UpdateGroupRequest
} from "src/network/graphql/groupService";
import { GroupUser } from "../groupDetail/groupDetailSlice";
import {
  createAttributeCatalog,
  getAttributeCatalog,
  updateAttributeCatalog
} from "src/network/graphql/transactionService";
import { AttributeCatalogItem, GroupType } from "src/types/commonTypes";
import { immediateToast } from "izitoast-react";
import { EMPTY_STRING } from "src/components/constant/constant";

export type Group = {
  group_id: string;
  name: string;
  description: string;
  group_type: GroupType;
  group_status: string;
  display_id: string;
  region_id: string;
  external_id?: string;
  region_code?: string;
  owner: {
    attributes: {
      first_name: string;
      last_name: string;
    };
    email: string;
    user_id: string;
  };
};

export const initialGroup: Group[] = [];

export type GroupPageMode = "SITE" | "COST_CENTRE" | "ALL";

export type GroupSectionState = {
  items: Group[];
  from: number;
  total: number;
  hasMore: boolean;
  loading: boolean;
  searchText: string;
  isArchive: boolean;
};

export type GroupAggregateStats = {
  users: number;
  wallets: number;
  cards: number;
};

const SINGLE_STREAM_PAGE_SIZE = 10;
const REGION_STREAM_PAGE_SIZE = 6;

export const createInitialGroupSectionState = (): GroupSectionState => ({
  items: [],
  from: 0,
  total: 0,
  hasMore: true,
  loading: false,
  searchText: EMPTY_STRING,
  isArchive: false
});

const mergeUniqueGroups = (existing: Group[], incoming: Group[]): Group[] => {
  const seenIds = new Set(existing.map((item) => item.group_id));
  const merged = [...existing];

  incoming.forEach((item) => {
    if (item.group_id && !seenIds.has(item.group_id)) {
      seenIds.add(item.group_id);
      merged.push(item);
    }
  });

  return merged;
};

const buildGroupFilterForMode = (
  pageMode: GroupPageMode,
  searchText: string,
  isArchive: boolean,
  regionId?: string
) => {
  const groupFilter: {
    group_type?: Exclude<GroupPageMode, "ALL">;
    group_status?: string;
    name?: string;
    region_id?: string;
  } = {};

  if (pageMode !== "ALL") {
    groupFilter.group_type = pageMode;
  }
  groupFilter.group_status = isArchive ? "INACTIVE" : "ACTIVE";

  if (searchText.trim()) {
    groupFilter.name = searchText.trim();
  }

  if (regionId) {
    groupFilter.region_id = regionId;
  }

  return { group_filter: groupFilter };
};

const groupSlice = createSlice({
  name: "group",
  initialState: {
    loading: false,
    error: null,
    group: {
      list: initialGroup,
      total: 0
    },
    createGroupLoading: false,
    regionCatalogLoading: false,
    createRegionLoading: false,
    updateRegionLoading: false,
    regionCatalog: {
      list: [] as AttributeCatalogItem[]
    },
    singleSection: createInitialGroupSectionState(),
    regionSections: {} as Record<string, GroupSectionState>,
    groupStatsById: {} as Record<string, GroupAggregateStats>,
    regionUsersById: {} as Record<string, { list: GroupUser[]; loading: boolean }>
  },
  reducers: {
    fetchGroupStart: (state) => {
      state.loading = true;
    },
    fetchGroupSuccess: (state, action) => {
      state.loading = false;
      state.group.list = action.payload?.list ?? [];
      state.group.total = action.payload?.total ?? 0;
    },
    fetchGroupFail: (state, action) => {
      state.loading = false;
      state.group.list = [];
      state.group.total = 0;
      state.error = action.payload;
    },
    createGroupStart: (state) => {
      state.createGroupLoading = true;
    },
    createGroupSuccess: (state) => {
      state.createGroupLoading = false;
    },
    createGroupFail: (state, action) => {
      state.createGroupLoading = false;
      state.error = action.payload;
    },
    updateGroupStart: (state) => {
      state.createGroupLoading = true;
    },
    updateGroupSuccess: (state) => {
      state.createGroupLoading = false;
    },
    updateGroupFail: (state, action) => {
      state.createGroupLoading = false;
      state.error = action.payload;
    },
    fetchRegionCatalogStart: (state) => {
      state.regionCatalogLoading = true;
    },
    fetchRegionCatalogSuccess: (state, action) => {
      state.regionCatalogLoading = false;
      state.regionCatalog.list = action.payload?.list ?? [];
    },
    fetchRegionCatalogFail: (state, action) => {
      state.regionCatalogLoading = false;
      state.regionCatalog.list = [];
      state.error = action.payload;
    },
    createRegionStart: (state) => {
      state.createRegionLoading = true;
    },
    createRegionSuccess: (state) => {
      state.createRegionLoading = false;
    },
    createRegionFail: (state, action) => {
      state.createRegionLoading = false;
      state.error = action.payload;
    },
    updateRegionStart: (state) => {
      state.updateRegionLoading = true;
    },
    updateRegionSuccess: (state) => {
      state.updateRegionLoading = false;
    },
    updateRegionFail: (state, action) => {
      state.updateRegionLoading = false;
      state.error = action.payload;
    },
    setSingleSectionLoading: (
      state,
      action: { payload: { reset: boolean; searchText: string; isArchive: boolean } }
    ) => {
      const { reset, searchText, isArchive } = action.payload;
      const nextState = reset ? createInitialGroupSectionState() : state.singleSection;

      state.singleSection = {
        ...nextState,
        searchText,
        isArchive,
        loading: true
      };
    },
    setSingleSectionData: (
      state,
      action: {
        payload: {
          items: Group[];
          from: number;
          total: number;
          hasMore: boolean;
          searchText: string;
          isArchive: boolean;
        };
      }
    ) => {
      const { items, from, total, hasMore, searchText, isArchive } = action.payload;

      state.singleSection = {
        items,
        from,
        total,
        hasMore,
        loading: false,
        searchText,
        isArchive
      };
    },
    setSingleSectionLoadFailed: (state, action) => {
      state.singleSection.loading = false;
      state.error = action.payload;
    },
    setRegionSectionLoading: (
      state,
      action: { payload: { regionId: string; reset: boolean; searchText: string; isArchive: boolean } }
    ) => {
      const { regionId, reset, searchText, isArchive } = action.payload;
      const base = reset
        ? createInitialGroupSectionState()
        : (state.regionSections[regionId] ?? createInitialGroupSectionState());

      state.regionSections[regionId] = {
        ...base,
        searchText,
        isArchive,
        loading: true
      };
    },
    setRegionSectionData: (
      state,
      action: {
        payload: {
          regionId: string;
          items: Group[];
          from: number;
          total: number;
          hasMore: boolean;
          searchText: string;
          isArchive: boolean;
        };
      }
    ) => {
      const { regionId, items, from, total, hasMore, searchText, isArchive } = action.payload;

      state.regionSections[regionId] = {
        items,
        from,
        total,
        hasMore,
        loading: false,
        searchText,
        isArchive
      };
    },
    setRegionSectionLoadFailed: (state, action: { payload: { regionId: string } }) => {
      const { regionId } = action.payload;
      const section = state.regionSections[regionId] ?? createInitialGroupSectionState();

      state.regionSections[regionId] = {
        ...section,
        loading: false
      };
    },
    removeRegionSections: (state, action: { payload: string[] }) => {
      action.payload.forEach((regionId) => {
        delete state.regionSections[regionId];
      });
    },
    upsertGroupStats: (state, action: { payload: Record<string, GroupAggregateStats> }) => {
      state.groupStatsById = {
        ...state.groupStatsById,
        ...action.payload
      };
    },
    invalidateGroupStats: (state, action: { payload: string[] }) => {
      action.payload.forEach((id) => {
        delete state.groupStatsById[id];
      });
    },
    setRegionUsersLoading: (state, action: { payload: string }) => {
      const regionId = action.payload;
      const existing = state.regionUsersById[regionId];

      state.regionUsersById[regionId] = { list: existing?.list ?? [], loading: true };
    },
    setRegionUsersData: (state, action: { payload: { regionId: string; list: GroupUser[] } }) => {
      const { regionId, list } = action.payload;

      state.regionUsersById[regionId] = { list, loading: false };
    },
    setRegionUsersFailed: (state, action: { payload: string }) => {
      const regionId = action.payload;
      const existing = state.regionUsersById[regionId];

      if (existing) existing.loading = false;
    },
    removeRegionUserFromState: (state, action: { payload: { regionId: string; userId: string } }) => {
      const { regionId, userId } = action.payload;
      const section = state.regionUsersById[regionId];

      if (section) {
        section.list = section.list.filter((u) => u.user_id !== userId);
      }
    }
  }
});

const {
  fetchGroupFail,
  fetchGroupStart,
  fetchGroupSuccess,
  fetchRegionCatalogFail,
  fetchRegionCatalogStart,
  fetchRegionCatalogSuccess,
  updateGroupFail,
  updateGroupStart,
  updateGroupSuccess,
  createRegionFail,
  createRegionStart,
  createRegionSuccess,
  updateRegionFail,
  updateRegionStart,
  updateRegionSuccess,
  createGroupFail,
  createGroupStart,
  createGroupSuccess,
  removeRegionSections,
  setRegionSectionData,
  setRegionSectionLoadFailed,
  setRegionSectionLoading,
  setSingleSectionData,
  setSingleSectionLoadFailed,
  setSingleSectionLoading,
  upsertGroupStats,
  setRegionUsersLoading,
  setRegionUsersData,
  setRegionUsersFailed,
  removeRegionUserFromState
} = groupSlice.actions;

export const { removeRegionUserFromState: removeRegionUser, invalidateGroupStats } = groupSlice.actions;

export const fetchGroupStatsAction = (groupIds: string[]) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const cached = getState().group.groupStatsById;
    const missingIds = Array.from(new Set(groupIds.filter((id): id is string => Boolean(id) && !cached[id])));

    if (missingIds.length === 0) return;

    const response = await aggGroupRelationship({
      from: 0,
      size: missingIds.length,
      filter: { relationship_filter: { group_ids: missingIds } }
    });
    const buckets = response.data.aggregate_group_relationship?.buckets ?? [];
    const mapped: Record<string, GroupAggregateStats> = {};

    buckets.forEach((bucket: { group_id: string; users: number; wallets: number; cards: number }) => {
      if (!bucket.group_id) return;
      mapped[bucket.group_id] = {
        users: bucket.users ?? 0,
        wallets: bucket.wallets ?? 0,
        cards: bucket.cards ?? 0
      };
    });

    if (Object.keys(mapped).length > 0) {
      dispatch(upsertGroupStats(mapped));
    }
  };
};

export const refreshGroupStatsAction = (groupIds: string[]) => {
  return async (dispatch: StoreDispatch) => {
    if (groupIds.length === 0) return;
    dispatch(invalidateGroupStats(groupIds));
    await dispatch(fetchGroupStatsAction(groupIds));
  };
};

const fetchAndStoreAggregateStats = async (dispatch: StoreDispatch, items: Group[]) => {
  const groupIds = Array.from(new Set(items.map((item) => item.group_id).filter(Boolean)));

  if (groupIds.length === 0) return;
  await dispatch(fetchGroupStatsAction(groupIds));
};

export const loadSingleSectionAction = (params: {
  reset?: boolean;
  searchText?: string;
  isArchive?: boolean;
  pageMode: GroupPageMode;
}) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const current = getState().group.singleSection;
    const reset = params.reset ?? false;
    const nextSearchText = params.searchText ?? current.searchText;
    const nextIsArchive = params.isArchive ?? current.isArchive;

    if (current.loading || (!reset && !current.hasMore)) return;

    const currentFrom = reset ? 0 : current.from;

    dispatch(setSingleSectionLoading({ reset, searchText: nextSearchText, isArchive: nextIsArchive }));

    try {
      const response = await getGroupList({
        from: currentFrom,
        size: SINGLE_STREAM_PAGE_SIZE,
        filter: buildGroupFilterForMode(params.pageMode, nextSearchText, nextIsArchive)
      });
      const incomingItems = response.data.search_group?.items ?? [];
      const total = response.data.search_group?.total_size ?? 0;
      const mergedItems = reset ? incomingItems : mergeUniqueGroups(current.items, incomingItems);

      dispatch(
        setSingleSectionData({
          items: mergedItems,
          from: currentFrom + incomingItems.length,
          total,
          hasMore: mergedItems.length < total,
          searchText: nextSearchText,
          isArchive: nextIsArchive
        })
      );

      await fetchAndStoreAggregateStats(dispatch, incomingItems);
    } catch (error) {
      dispatch(setSingleSectionLoadFailed(error));
    }
  };
};

export const loadRegionSectionAction = (params: {
  regionId: string;
  reset?: boolean;
  searchText?: string;
  isArchive?: boolean;
  pageMode: GroupPageMode;
}) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const { regionId } = params;
    const section = getState().group.regionSections[regionId] ?? createInitialGroupSectionState();
    const reset = params.reset ?? false;
    const nextSearchText = params.searchText ?? section.searchText;
    const nextIsArchive = params.isArchive ?? section.isArchive;

    if (section.loading || (!reset && !section.hasMore)) return;

    const currentFrom = reset ? 0 : section.from;

    dispatch(
      setRegionSectionLoading({
        regionId,
        reset,
        searchText: nextSearchText,
        isArchive: nextIsArchive
      })
    );

    try {
      const response = await getGroupList({
        from: currentFrom,
        size: REGION_STREAM_PAGE_SIZE,
        filter: buildGroupFilterForMode(params.pageMode, nextSearchText, nextIsArchive, regionId)
      });
      const incomingItems = response.data.search_group?.items ?? [];
      const total = response.data.search_group?.total_size ?? 0;
      const mergedItems = reset ? incomingItems : mergeUniqueGroups(section.items, incomingItems);

      dispatch(
        setRegionSectionData({
          regionId,
          items: mergedItems,
          from: currentFrom + incomingItems.length,
          total,
          hasMore: mergedItems.length < total,
          searchText: nextSearchText,
          isArchive: nextIsArchive
        })
      );

      await fetchAndStoreAggregateStats(dispatch, incomingItems);
    } catch (error) {
      dispatch(setRegionSectionLoadFailed({ regionId }));
    }
  };
};

export const hydrateRegionSelectionAction = (params: {
  addedIds: string[];
  removedIds: string[];
  pageMode: GroupPageMode;
}) => {
  return async (dispatch: StoreDispatch) => {
    if (params.removedIds.length > 0) {
      dispatch(removeRegionSections(params.removedIds));
    }

    if (params.addedIds.length > 0) {
      for (const regionId of params.addedIds) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(loadRegionSectionAction({ regionId, reset: true, pageMode: params.pageMode }));
      }
    }
  };
};

export const fetchGroupListAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupStart());
    try {
      const response = await getGroupList(data);

      const structuredResponse = response.data.search_group?.items;

      dispatch(
        fetchGroupSuccess({
          list: structuredResponse,
          total: response.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchGroupFail(error));
    }
  };
};

export const fetchRegionCatalogAction = (title?: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchRegionCatalogStart());
    try {
      const trimmedTitle = title?.trim();
      const response = await getAttributeCatalog({
        from: 0,
        size: 200,
        attribute_catalog_filter: {
          type: "REGION",
          ...(trimmedTitle ? { title: trimmedTitle } : {})
        }
      });
      const items = response.data.search_attribute_catalog?.items ?? [];

      dispatch(fetchRegionCatalogSuccess({ list: items }));
    } catch (error) {
      dispatch(fetchRegionCatalogFail(error));
    }
  };
};

export const createRegionCatalogAction = (title: string, code: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createRegionStart());
    try {
      await createAttributeCatalog({
        title,
        code,
        type: "REGION"
      });
      dispatch(createRegionSuccess());
      dispatch(fetchRegionCatalogAction());
    } catch (error) {
      dispatch(createRegionFail(error));
    }
  };
};

export const updateRegionCatalogAction = (regionId: string, title: string, code: string, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateRegionStart());
    try {
      await updateAttributeCatalog({
        attribute_catalog_id: regionId,
        title,
        code,
        type: "REGION",
        status: "ACTIVE"
      });
      dispatch(updateRegionSuccess());
      dispatch(fetchRegionCatalogAction());
      immediateToast("success", {
        message: "Area updated successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      onSuccess?.();
    } catch (error) {
      dispatch(updateRegionFail(error));
    }
  };
};

export const updateGroupAction = (data: UpdateGroupRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateGroupStart());
    try {
      await updateGroup(data);

      dispatch(updateGroupSuccess());
      immediateToast("success", {
        message: "Group Updated Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (err) {
      dispatch(updateGroupFail(err));
    }
  };
};

export const createGroupAction = (data: CreateGroupRequest, cb?: (_groupId: string) => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createGroupStart());
    try {
      const response = await createGroup(data);

      await createGroupWallet(data?.description, data?.name, response.data.create_group.group_id);

      dispatch(createGroupSuccess());
      immediateToast("success", {
        message: "Group Successfully Created.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb(response.data.create_group.group_id);
    } catch (error) {
      dispatch(createGroupFail(error));
    }
  };
};

export const createGroupWalletAction = (
  description: string,
  name: string,
  groupId: string,
  cb?: (_walletId: string) => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createGroupStart());
    try {
      const response = await createGroupWallet(description, name, groupId);

      dispatch(createGroupSuccess());
      immediateToast("success", {
        message: "Group Card Successfully Created.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb(response.data.create_group_wallet.wallet_id);
    } catch (error) {
      dispatch(createGroupFail(error));
    }
  };
};

export const fetchRegionUsersAction = (regionId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(setRegionUsersLoading(regionId));
    try {
      const response = await getRegionUsers(regionId);
      const list = response.data.get_region_users ?? [];

      dispatch(setRegionUsersData({ regionId, list }));
    } catch (error) {
      dispatch(setRegionUsersFailed(regionId));
    }
  };
};

export const connectUsersToRegionAction = (userIds: string[], regionId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    try {
      for (const userId of userIds) {
        await connectUserToRegion(userId, regionId);
      }
      immediateToast("success", {
        message: userIds.length > 1 ? `${userIds.length} users connected to region.` : "User connected to region.",
        timeout: 3000,
        position: "topCenter"
      });
      await dispatch(fetchRegionUsersAction(regionId));
      cb?.();

      const groupIds = (getState().group.regionSections[regionId]?.items ?? []).map((g) => g.group_id).filter(Boolean);

      dispatch(refreshGroupStatsAction(groupIds));
    } catch (error) {
      immediateToast("error", {
        message: "Failed to connect user to region.",
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const removeUsersFromRegionAction = (userIds: string[], regionId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    try {
      await Promise.all(userIds.map((userId) => removeUserFromRegion(userId, regionId)));
      userIds.forEach((userId) => dispatch(removeRegionUserFromState({ regionId, userId })));
      immediateToast("success", {
        message: userIds.length > 1 ? `${userIds.length} users removed from region.` : "User removed from region.",
        timeout: 3000,
        position: "topCenter"
      });
      cb?.();

      const groupIds = (getState().group.regionSections[regionId]?.items ?? []).map((g) => g.group_id).filter(Boolean);

      dispatch(refreshGroupStatsAction(groupIds));
    } catch (error) {
      immediateToast("error", {
        message: "Failed to remove users from region.",
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export default groupSlice.reducer;
